/**
 * Owner: Haselton Baker Risk Group, LLC
 * Copyright All Rights Reserved
 */
import pipe from 'lodash/fp/pipe.js';
import { connect } from 'react-redux';
import withNavigate from '#components/support/routing/withNavigate/index.jsx';
import { MODELS, UPLOAD_DECISION } from '#constants/pathStrings.js';
import { EXTRACTION_CAPABLE_TYPES } from '#constants/models/extractionCapableTypes.js';
import { createModel } from '#actions/models/model.js';
import { startCreateModelFlow } from '#actions/models/createModelFlow.js';
import {
  selectAuthUserId,
  selectAuthUserModelTypes,
  selectAuthUserOrgHasExtractionAccess,
  selectAuthUserOrgId,
  selectAuthUserPermissions,
} from '#selectors/entities/users.js';
import ModelTypesPage from './ModelTypesPage.jsx';

export const mapStateToProps = state => ({
  createdById: selectAuthUserId(state),
  hasExtractionAccess: selectAuthUserOrgHasExtractionAccess(state),
  orgId: selectAuthUserOrgId(state),
  permissions: selectAuthUserPermissions(state),
  // The org's permitted type ids: the page offers a Create button only for types the org may
  // actually create, since POST /model rejects anything else with a 403.
  modelTypes: selectAuthUserModelTypes(state),
});

const mapDispatchToProps = { createModel, startCreateModelFlow };

// Choosing a type creates the model immediately (UI design §2.1; issue #127 removed the interim
// naming step). Types 1 and 2 then route into the create flow's decision step (HBR-4950 #316) for
// the newly created model, which needs the real modelId (`extraction-uploads/{modelId}/...`). No
// name is entered at creation -- a model's name is an ordinary in-form field the user can set
// later (and extraction may propose).
export const mergeProps = (stateProps, dispatchProps, ownProps) => {
  const { createdById, hasExtractionAccess, modelTypes, orgId, permissions } = stateProps;
  const { navigate } = ownProps;
  return {
    ...ownProps,
    modelTypes,
    handleCancelClick: () => navigate(`/${MODELS}`),
    handleContinueClick: type =>
      dispatchProps.createModel(type, createdById, orgId, permissions).then(model => {
        if (EXTRACTION_CAPABLE_TYPES.includes(type) && hasExtractionAccess) {
          dispatchProps.startCreateModelFlow(model.uuid);
          navigate(`/${MODELS}/${model.uuid}/${UPLOAD_DECISION}`);
        } else {
          navigate(`/${MODELS}/${model.uuid}`);
        }
        return model;
      }),
  };
};

export default pipe(
  connect(mapStateToProps, mapDispatchToProps, mergeProps),
  withNavigate
)(ModelTypesPage);
